import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, downloadWithToast, triggerFileDownload } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, FilePdf, Receipt } from "@phosphor-icons/react";

const HEAD_ORDER = ["VRV", "Light Commercial", "Room Air Conditioners"];
const headSlug = (h) => h.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const headOf = (x) => x.group || "VRV";
const pickerTabCls = (active) =>
  `px-2.5 py-1 rounded-sm text-[11px] font-semibold border transition-colors ${
    active
      ? "bg-[#0F172A] text-white border-[#0F172A]"
      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
  }`;

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-50 text-blue-700",
  accepted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-amber-50 text-amber-700",
  converted: "bg-purple-50 text-purple-700",
};

// iter71 — Give every line item a stable client-side key so React reconciliation
// survives reorders/filters (index-as-key would drop input focus & state).
let _lineKey = 0;
const nextLineKey = () => `ln-${Date.now().toString(36)}-${(++_lineKey).toString(36)}`;
const withKey = (item) => ({ _key: item._key || nextLineKey(), ...item });

// iter70 — VRV-style discounting does not apply to Room AC / Sky-Air (SA/PA)
const isDiscountExempt = (cat) => {
  const c = (cat || "").trim();
  return c.startsWith("Room AC") || c.startsWith("Sky Air");
};

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TotalRow({ label, value, big, muted }) {
  return (
    <div className={`flex items-center justify-between ${big ? "text-[15px] font-bold text-[#EA580C]" : "text-[12px]"} ${muted ? "text-slate-500" : "text-slate-900"} py-0.5`}>
      <span>{label}</span>
      <span className="font-mono-num">{fmtMoney(value)}</span>
    </div>
  );
}

function SkuPickerDialog({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [head, setHead] = useState("");
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [qtyMap, setQtyMap] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: c }] = await Promise.all([
        api.get("/price-list", { params: { q, category: cat, limit: 1000 } }),
        api.get("/price-list/categories"),
      ]);
      setRows(r || []);
      setCats(c || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }, [q, cat]);

  useEffect(() => { load(); }, [load]);

  const heads = useMemo(() => {
    const m = {};
    cats.forEach((c) => { const g = headOf(c); m[g] = (m[g] || 0) + c.count; });
    return HEAD_ORDER.filter((h) => m[h]).map((h) => ({ name: h, count: m[h] }));
  }, [cats]);

  const visibleRows = useMemo(
    () => (head ? rows.filter((r) => headOf(r) === head) : rows),
    [rows, head]
  );

  const groupedRows = useMemo(() => {
    const order = head ? [head] : HEAD_ORDER;
    const map = {};
    visibleRows.forEach((r) => {
      const g = headOf(r);
      (map[g] = map[g] || []).push(r);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => (a.category || "").localeCompare(b.category || "") || (a.model || "").localeCompare(b.model || ""))
    );
    return order.filter((g) => map[g]?.length).map((g) => [g, map[g]]);
  }, [visibleRows, head]);

  // Category options filtered by the selected head — memoized to skip
  // recomputing on every keystroke.
  const catOptions = useMemo(
    () => cats.filter((c) => !head || headOf(c) === head),
    [cats, head]
  );

  const pickHead = (h) => { setHead(h); setCat(""); };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sku-picker-dialog">
        <DialogHeader><DialogTitle className="font-display">Pick SKUs from Daikin Catalog</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="picker-head-tabs">
          <button type="button" onClick={() => pickHead("")} className={pickerTabCls(head === "")} data-testid="picker-head-tab-all">
            All <span className="ml-1 opacity-60 font-mono-num">{rows.length}</span>
          </button>
          {heads.map((h) => (
            <button
              key={h.name}
              type="button"
              onClick={() => pickHead(h.name)}
              className={pickerTabCls(head === h.name)}
              data-testid={`picker-head-tab-${headSlug(h.name)}`}
            >
              {h.name} <span className="ml-1 opacity-60 font-mono-num">{h.count}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <Input placeholder="Search model / description…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-72 text-[12px]" data-testid="picker-search" autoFocus />
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="border border-slate-200 rounded-sm h-8 text-[11.5px] px-2" data-testid="picker-cat">
            <option value="">All categories</option>
            {catOptions.map((c) => <option key={c.category} value={c.category}>{c.category} ({c.count})</option>)}
          </select>
          <div className="text-[10.5px] text-slate-500">{visibleRows.length} results</div>
        </div>
        <div className="flex-1 overflow-y-auto border border-slate-200 rounded-sm mt-2">
          <Table className="text-[11px]">
            <TableHeader>
              <TableRow className="bg-slate-100 sticky top-0 z-10">
                <TableHead className="w-32">Model</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-24 text-right">Price ₹</TableHead>
                <TableHead className="w-24 text-center">Qty</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No matching SKUs</TableCell></TableRow>
              )}
              {groupedRows.map(([gname, grows]) => (
                <React.Fragment key={gname}>
                  <TableRow className="bg-slate-100 hover:bg-slate-100" data-testid={`picker-group-header-${headSlug(gname)}`}>
                    <TableCell colSpan={5} className="py-1">
                      <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-600">{gname}</span>
                      <span className="ml-2 text-[9.5px] text-slate-400 font-mono-num">{grows.length} SKUs</span>
                    </TableCell>
                  </TableRow>
                  {grows.map((r) => (
                    <TableRow key={r.id} data-testid={`picker-row-${r.model}`}>
                      <TableCell className="font-mono font-semibold">
                        {r.model}
                        {r.star_rating && (
                          <span className="ml-1 inline-block px-1 rounded-sm text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300">★ {r.star_rating}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[10.5px]">
                        <div className="text-slate-500">{r.category}</div>
                        <div>{r.description}</div>
                      </TableCell>
                      <TableCell className="text-right font-mono-num font-bold text-[#EA580C]">{fmtMoney(r.celsius_price)}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          value={qtyMap[r.id] || 1}
                          onChange={(e) => setQtyMap({ ...qtyMap, [r.id]: parseFloat(e.target.value) || 1 })}
                          className="h-7 text-center text-[11px]"
                          data-testid={`picker-qty-${r.model}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onPick(r, qtyMap[r.id] || 1)} data-testid={`picker-add-${r.model}`}>
                          <Plus size={11} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function QuoteEditorSheet({ quoteId, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_name: "", site_address: "", contact_no: "", attention: "", email: "",
    subject: "Quotation for Daikin VRV System",
    date: new Date().toISOString().slice(0, 10),
    valid_until: "",
    items: [],
    global_discount_pct: 0,
    gst_pct: 18,
    notes: "",
    terms: [
      "Prices are ex-GST; GST @18% is charged extra as shown in the Summary.",
      "This quotation is valid until the date mentioned above.",
      "Prices are subject to change as per Daikin's prevailing price list.",
      "Delivery & installation timelines to be mutually agreed at order confirmation.",
      "Payment terms: as per agreed commercial terms.",
    ],
    status: "draft",
  });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedLines, setSelectedLines] = useState({});

  useEffect(() => {
    if (!quoteId) return;
    (async () => {
      try {
        const { data } = await api.get(`/quotations/${quoteId}`);
        setQuote(data);
        setForm({
          client_name: data.client_name || "",
          site_address: data.site_address || "",
          contact_no: data.contact_no || "",
          attention: data.attention || "",
          email: data.email || "",
          subject: data.subject || "",
          date: data.date,
          valid_until: data.valid_until,
          items: (data.items || []).map(withKey),
          global_discount_pct: data.global_discount_pct || 0,
          gst_pct: data.gst_pct || 18,
          notes: data.notes || "",
          terms: data.terms || [],
          status: data.status || "draft",
        });
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, [quoteId]);

  const totals = useMemo(() => {
    const gst_pct = parseFloat(form.gst_pct) || 18;
    const globalDisc = parseFloat(form.global_discount_pct) || 0;
    const gFactor = Math.max(0, 1 - globalDisc / 100);
    let subtotal_list = 0;
    let subtotal_after_line = 0;
    let non_exempt_after_line = 0;
    let total_gst_eff = 0;
    for (const ln of form.items) {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.unit_price) || 0;
      const exempt = isDiscountExempt(ln.category);
      const lineDisc = exempt ? 0 : (parseFloat(ln.discount_pct) || 0);
      const lFactor = Math.max(0, 1 - lineDisc / 100);
      const lineGstFactor = exempt ? 1 : gFactor;
      const list_amt = qty * price;
      const line_amt = list_amt * lFactor;
      const line_gst_pct = parseFloat(ln.gst_pct) || gst_pct;
      subtotal_list += list_amt;
      subtotal_after_line += line_amt;
      if (!exempt) non_exempt_after_line += line_amt;
      total_gst_eff += line_amt * lineGstFactor * (line_gst_pct / 100);
    }
    const exempt_after_line = subtotal_after_line - non_exempt_after_line;
    const total_ex_gst = non_exempt_after_line * gFactor + exempt_after_line;
    const global_discount_amount = subtotal_list - total_ex_gst;
    return { subtotal: subtotal_list, global_discount_amount, total_ex_gst, total_gst: total_gst_eff, grand_total: total_ex_gst + total_gst_eff };
  }, [form.items, form.global_discount_pct, form.gst_pct]);

  const patchItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const addItem = (sku, qty = 1) => {
    setForm({
      ...form,
      items: [...form.items, withKey({
        price_list_id: sku.id,
        category: sku.category,
        model: sku.model,
        description: sku.description || "",
        star_rating: sku.star_rating || "",
        unit: sku.unit || "Nos",
        hsn: sku.hsn || "",
        unit_price: sku.celsius_price,
        qty: qty,
        gst_pct: sku.gst_pct || 18,
        discount_pct: 0,
      })],
    });
  };

  const save = async (downloadPdfAfter = false) => {
    setBusy(true);
    try {
      const payload = { ...form };
      let saved;
      if (quoteId) {
        const { data } = await api.put(`/quotations/${quoteId}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/quotations", payload);
        saved = data;
      }
      toast.success(`Saved ${saved.quote_no}`);
      if (downloadPdfAfter) {
        await downloadWithToast(`/quotations/${saved.id}/pdf`, `${saved.quote_no}.pdf`);
      }
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const selectedCount = Object.values(selectedLines).filter(Boolean).length;
  const saveLabel = busy ? "Saving…" : "Save";

  const applyPreset = (p) => {
    // iter70 — presets skip discount-exempt (RA/SA/PA) lines
    const items = form.items.map((ln, i) =>
      selectedLines[i] && !isDiscountExempt(ln.category) ? { ...ln, discount_pct: p } : ln
    );
    setForm({ ...form, items });
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent className="w-[min(1200px,100vw)] sm:max-w-none overflow-y-auto" data-testid="quote-editor-sheet">
        <SheetHeader className="mb-3">
          <SheetTitle className="font-display flex items-center gap-2 flex-wrap">
            <Receipt size={18} className="text-[#EA580C]" />
            {quote ? `Edit · ${quote.quote_no}` : "New Quotation"}
            {quote && (
              <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[quote.status] || "bg-slate-100"}`}>
                {quote.status}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          {/* Client + meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Client Name">
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="quote-client-name" />
            </Field>
            <Field label="Attention">
              <Input value={form.attention} onChange={(e) => setForm({ ...form, attention: e.target.value })} />
            </Field>
            <Field label="Site Address">
              <Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Contact No.">
              <Input value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} />
            </Field>
            <Field label="Subject">
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </Field>
            <Field label="Date">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Valid Until">
              <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </Field>
          </div>

          {/* Items table */}
          <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
            <h3 className="text-[12.5px] font-semibold uppercase tracking-widest text-slate-700">Line Items ({form.items.length})</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedCount > 0 && (
                <div className="flex items-center gap-1 border border-slate-200 rounded-sm px-2 py-1 bg-amber-50/60" data-testid="discount-presets-bar">
                  <span className="text-[10.5px] text-slate-600 mr-1">
                    Apply to {selectedCount} line{selectedCount > 1 ? "s" : ""}:
                  </span>
                  {[0, 5, 10, 15].map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`preset-${p}pct-btn`}
                      onClick={() => applyPreset(p)}
                      className="px-2 py-0.5 text-[10.5px] rounded-full font-semibold transition-colors bg-white border border-slate-300 hover:bg-[#EA580C] hover:text-white hover:border-[#EA580C] cursor-pointer"
                    >
                      {p === 0 ? "Clear" : `${p}%`}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSelectedLines({})}
                    className="ml-1 text-[10px] text-slate-400 hover:text-slate-700 underline"
                    data-testid="clear-selection-btn"
                  >clear</button>
                </div>
              )}
              <Button onClick={() => setPickerOpen(true)} size="sm" className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 text-white h-8 text-[11.5px]" data-testid="add-item-btn">
                <Plus size={12} className="mr-1" /> Add SKU
              </Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm overflow-x-auto">
            <Table className="text-[11px]">
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-8">
                    <input
                      type="checkbox"
                      data-testid="select-all-lines"
                      checked={form.items.length > 0 && form.items.every((_, i) => selectedLines[i])}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const all = {};
                          form.items.forEach((_, i) => { all[i] = true; });
                          setSelectedLines(all);
                        } else setSelectedLines({});
                      }}
                      className="h-3.5 w-3.5 accent-[#EA580C] cursor-pointer"
                    />
                  </TableHead>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-28">Model</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-16 text-center">Unit</TableHead>
                  <TableHead className="w-24 text-right">Unit Price</TableHead>
                  <TableHead className="w-16 text-right">Qty</TableHead>
                  <TableHead className="w-16 text-right" title="Per-line discount % (applied on top of global, on ex-GST base). Never shown on client PDF.">Disc%</TableHead>
                  <TableHead className="w-16 text-right">GST%</TableHead>
                  <TableHead className="w-28 text-right">Amount</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.items.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">
                    No line items yet. Click "Add SKU" above to pick from the Daikin catalog.
                  </TableCell></TableRow>
                )}
                {form.items.map((ln, i) => {
                  const qty = parseFloat(ln.qty) || 0;
                  const price = parseFloat(ln.unit_price) || 0;
                  const exempt = isDiscountExempt(ln.category);
                  const lineDisc = exempt ? 0 : (parseFloat(ln.discount_pct) || 0);
                  const amount = qty * price * (1 - lineDisc / 100);
                  return (
                    <TableRow key={ln._key || `line-${i}`} data-testid={`quote-line-${i}`} className={selectedLines[i] ? "bg-amber-50/50" : ""}>
                      <TableCell>
                        <input
                          type="checkbox"
                          data-testid={`select-line-${i}`}
                          checked={!!selectedLines[i]}
                          onChange={(e) => setSelectedLines({ ...selectedLines, [i]: e.target.checked })}
                          className="h-3.5 w-3.5 accent-[#EA580C] cursor-pointer"
                        />
                      </TableCell>
                      <TableCell className="font-mono">{i + 1}</TableCell>
                      <TableCell className="font-mono font-semibold">{ln.model}</TableCell>
                      <TableCell className="text-[10.5px] text-slate-600">
                        <div>{ln.category}</div>
                        <div>
                          {ln.description}
                          {ln.star_rating && (
                            <span className="ml-1 inline-block px-1 rounded-sm text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300">★ {ln.star_rating}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{ln.unit}</TableCell>
                      <TableCell>
                        <Input type="number" value={ln.unit_price} onChange={(e) => patchItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={ln.qty} onChange={(e) => patchItem(i, "qty", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" data-testid={`qty-${i}`} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" min={0} max={90} step={0.5} value={exempt ? 0 : (ln.discount_pct || 0)} onChange={(e) => patchItem(i, "discount_pct", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" data-testid={`quote-line-disc-${i}`} disabled={exempt} title={exempt ? "Discount not applicable — Room AC / Sky-Air rates are already bottom-line" : "Per-line discount %"} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={ln.gst_pct} onChange={(e) => patchItem(i, "gst_pct", parseFloat(e.target.value) || 0)} className="h-7 text-right text-[11px] px-1.5" />
                      </TableCell>
                      <TableCell className="text-right font-mono-num font-semibold" title={lineDisc > 0 ? `List: ${fmtMoney(qty * price)} · ${lineDisc}% off` : undefined}>{fmtMoney(amount)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600" onClick={() => removeItem(i)} data-testid={`remove-line-${i}`}>
                          <Trash size={11} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
            <div className="flex items-end gap-3">
              <Field label="Discount % (internal)" className="w-40">
                <Input data-testid="quote-discount-pct" type="number" min={0} max={90} step={0.5} value={form.global_discount_pct} onChange={(e) => setForm({ ...form, global_discount_pct: parseFloat(e.target.value) || 0 })} className="h-8 text-right" />
                <div className="text-[9.5px] text-slate-400 mt-0.5">VRV & others only — not RA/SA/PA</div>
              </Field>
              <Field label="GST %" className="w-20">
                <Input type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: parseFloat(e.target.value) || 18 })} className="h-8 text-right" />
              </Field>
            </div>
            <div className="border-2 border-[#0F172A] rounded-sm px-4 py-3 min-w-[300px]" data-testid="quote-totals">
              <TotalRow label="Subtotal (ex-GST)" value={totals.subtotal} />
              {totals.global_discount_amount > 0 && (
                <TotalRow label={`Discount (${form.global_discount_pct}%) · internal`} value={-totals.global_discount_amount} muted />
              )}
              <TotalRow label={`GST @ ${form.gst_pct}%`} value={totals.total_gst} muted />
              <div className="border-t border-slate-300 my-1"></div>
              <TotalRow label="GRAND TOTAL" value={totals.grand_total} big />
              {totals.global_discount_amount > 0 && (
                <div className="text-[10px] text-slate-500 mt-1 italic">Discount is baked into unit rate on the client PDF — no separate line will appear.</div>
              )}
            </div>
          </div>

          {/* Notes */}
          <Field label="Notes (optional)">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="text-[11.5px]" rows={2} />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 mt-4 border-t border-slate-200 sticky bottom-0 bg-white z-10">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save(false)} disabled={busy} className="rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90 text-white ml-auto" data-testid="quote-save">
            {saveLabel}
          </Button>
          <Button onClick={() => save(true)} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="quote-save-pdf">
            <FilePdf size={13} className="mr-1" /> Save & Download PDF
          </Button>
        </div>

        {pickerOpen && <SkuPickerDialog onClose={() => setPickerOpen(false)} onPick={(sku, qty) => { addItem(sku, qty); }} />}
      </SheetContent>
    </Sheet>
  );
}
